import { fromJS } from 'immutable';
import { normalize } from 'normalizr';
import objectAssign from 'object-assign';

import { mapListToMaps, normalizeList, websocketsHandlerFactory } from '@sophocles/entities/entityUtils';
import {
  receiveIntrusionSensorConfigurations,
  sayDeleteIntrusionSensorConfiguration,
} from '@sophocles/entities/IntrusionSensorConfiguration';
import { receiveRadios } from '@sophocles/entities/Radio';
import { syncRelaysForController } from '@sophocles/entities/Relay';
import { receiveRelayControllerFirmwareVersions } from '@sophocles/entities/RelayControllerFirmwareVersion';
import { receiveRelaySchedules } from '@sophocles/entities/RelaySchedule';
import { receiveSmartlinkModels } from '@sophocles/entities/SmartlinkModel';
import { SmartlinkUnit, smartlinkUnitSchema } from '@sophocles/entities/SmartlinkUnit/smartlinkUnitRecord';
import { addToast } from '@sophocles/features/toast';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';

/* REQUEST ACTIONS */
export const REQUEST_SMARTLINK_UNIT = 'REQUEST_SMARTLINK_UNIT'; // singular
export function requestSmartlinkUnit(unit_id) {
  return {
    type: REQUEST_SMARTLINK_UNIT,
    smartlink: unit_id,
  };
}

/* RECEIVE ACTIONS */
export const RECEIVE_SMARTLINK_UNITS = 'RECEIVE_SMARTLINK_UNITS'; // plural receive
export function receiveSmartlinkUnits(json) {
  return {
    type: RECEIVE_SMARTLINK_UNITS,
    smartlinkUnits: mapListToMaps(json, SmartlinkUnit),
  };
}

export const REQUEST_SMARTLINK_UNIT_FOR_SITE = 'REQUEST_SMARTLINK_UNIT_FOR_SITE';
function requestSmartlinkUnitForSite(site) {
  return {
    type: REQUEST_SMARTLINK_UNIT_FOR_SITE,
    site,
  };
}

/* FETCH ACTIONS */

// Dispatch a per-controller relay sync for every unit in the normalized payload.
// Authoritative replace: prunes stale relays that are no longer on the unit, then
// merges in the fresh ones. Must run AFTER unit/sensor receivers to avoid dangling
// refs if selectors run mid-batch.
function syncRelaysFromPayload(dispatch, json) {
  const units = json.entities.smartlinkUnits || {};
  const relaysById = json.entities.relays || {};
  Object.keys(units).forEach(unitId => {
    const unitRelayIds = units[unitId].relays || [];
    const unitRelays = {};
    unitRelayIds.forEach(rid => {
      if (relaysById[rid]) unitRelays[rid] = relaysById[rid];
    });
    dispatch(syncRelaysForController(unitId, unitRelays));
  });
}

export function fetchSmartlinkUnitForSite(site_id) {
  return dispatch => {
    dispatch(requestSmartlinkUnitForSite(site_id));
    return apiClient
      .get(`/relaycontrollers/?site=${site_id}`)
      .then(response => normalizeList(response.data.results, 'smartlinkUnits', smartlinkUnitSchema))
      .then(json => {
        dispatch(receiveRadios(json.entities.radios));
        dispatch(receiveRelaySchedules(json.entities.relaySchedules));
        dispatch(receiveIntrusionSensorConfigurations(json.entities.intrusionSensorConfigurations));
        dispatch(receiveSmartlinkModels(json.entities.smartlinkModels));
        dispatch(receiveRelayControllerFirmwareVersions(json.entities.relayControllerFirmwareVersions));
        dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
        syncRelaysFromPayload(dispatch, json);
        return json;
      });
  };
}

export function fetchSmartlinkUnit(unit_id) {
  return dispatch => {
    dispatch(requestSmartlinkUnit(unit_id));
    return apiClient
      .get(`/relaycontrollers/${unit_id}/`)
      .then(response => normalize(response.data, smartlinkUnitSchema))
      .then(json => {
        dispatch(receiveRadios(json.entities.radios));
        dispatch(receiveRelaySchedules(json.entities.relaySchedules));
        dispatch(receiveIntrusionSensorConfigurations(json.entities.intrusionSensorConfigurations));
        dispatch(receiveSmartlinkModels(json.entities.smartlinkModels));
        dispatch(receiveRelayControllerFirmwareVersions(json.entities.relayControllerFirmwareVersions));
        dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
        syncRelaysFromPayload(dispatch, json);
        return json;
      });
  };
}

/* Edit Actions */
export const EDIT_RELAY_CONTROLLER = 'EDIT_RELAY_CONTROLLER';
export function editRelayController(relayController, data) {
  return {
    type: EDIT_RELAY_CONTROLLER,
    relayController,
    data,
  };
}

export function patchRelayController(relayController_id, values) {
  return dispatch => {
    dispatch(editRelayController(relayController_id, values));
    return apiClient
      .patch(`/relaycontrollers/${relayController_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, smartlinkUnitSchema))
      .then(json => dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits)))
      .catch(error => dispatch(genericErrorAction(error, EDIT_RELAY_CONTROLLER)));
  };
}

/* Create Actions */
export const CREATE_RELAY_CONTROLLER = 'CREATE_RELAY_CONTROLLER';
function sayCreateRelayController(values) {
  return {
    type: CREATE_RELAY_CONTROLLER,
    values: values,
  };
}

export function createRelayController(values) {
  return dispatch => {
    dispatch(sayCreateRelayController(values));
    return apiClient
      .post(`/relaycontrollers/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, smartlinkUnitSchema))
      .then(json => dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits)))
      .catch(error => dispatch(genericErrorAction(error, CREATE_RELAY_CONTROLLER)));
  };
}

/* Delete Actions */
export const DELETE_RELAY_CONTROLLER = 'DELETE_RELAY_CONTROLLER';
function sayDeleteRelayController(relayController_id) {
  return {
    type: DELETE_RELAY_CONTROLLER,
    relayController: relayController_id,
  };
}

export function deleteRelayController(relayController_id) {
  return dispatch =>
    apiClient
      .delete(`/relaycontrollers/${relayController_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteRelayController(relayController_id)))
      .catch(error => dispatch(genericErrorAction(error, DELETE_RELAY_CONTROLLER)));
}

const smartlinkUnitReceiver = (dispatch, json) => {
  dispatch(receiveRadios(json.entities.radios));
  dispatch(receiveIntrusionSensorConfigurations(json.entities.intrusionSensorConfigurations));
  dispatch(receiveSmartlinkModels(json.entities.smartlinkModels));
  dispatch(receiveRelayControllerFirmwareVersions(json.entities.relayControllerFirmwareVersions));
  const result = dispatch(receiveSmartlinkUnits(json.entities.smartlinkUnits));
  syncRelaysFromPayload(dispatch, json);
  return result;
};

export const handleRelayControllerSocketActions = websocketsHandlerFactory(
  smartlinkUnitReceiver,
  sayDeleteRelayController,
  smartlinkUnitSchema,
);

// Firmware-upgrade live progress channel. Payload shape from
// pliny.tasks.upgrades._update_log:
//   { relay_controller_id, current_upgrade: { batch_id, ... } | null }
// Goes through a dedicated reducer action instead of the standard
// relaycontroller websockets handler so it doesn't trip
// syncRelaysForController (which would wipe the unit's relays when
// the partial payload has no `relays` key).
export const UPDATE_SMARTLINK_UNIT_CURRENT_UPGRADE = 'UPDATE_SMARTLINK_UNIT_CURRENT_UPGRADE';
function updateSmartlinkUnitCurrentUpgrade(relayControllerId, currentUpgrade) {
  return {
    type: UPDATE_SMARTLINK_UNIT_CURRENT_UPGRADE,
    relayControllerId,
    currentUpgrade,
  };
}

export const handleUpgradeProgressSocketActions = (operation, data) => dispatch => {
  // Op is '+' for every progress tick; '-' isn't used here. The
  // payload always carries the full current_upgrade snapshot (or
  // null when the unit just finished), so we just set the field.
  if (!data || data.relay_controller_id == null) return;
  dispatch(updateSmartlinkUnitCurrentUpgrade(data.relay_controller_id, data.current_upgrade ?? null));
};

// Custom web sockets handler because we need have a slightly diffreent use case.
// bring in the smartlink unit schema and receiver, then just turn the intrusion object into a relaycontroller object on + operation.
function websocketsHandler(receiveAction, deleteAction, schema, key) {
  return (operation, data) => dispatch => {
    if (operation === '-') {
      dispatch(deleteAction(data.id));
    } else if (operation === '+') {
      // expected structure is {id:'x', relay_controller: ... }
      // need to pull relay_controller out and put the intrusion inside of it.
      const controller = objectAssign({}, data.relay_controller);
      const intrusionSensor = objectAssign({}, data);
      delete intrusionSensor.relay_controller;
      controller.intrusion_sensor_configuration = intrusionSensor;
      const json = normalize(controller, schema);
      receiveAction(dispatch, json);
    }
  };
}

export const handleIntrusionSensorConfigurationActions = websocketsHandler(
  smartlinkUnitReceiver,
  sayDeleteIntrusionSensorConfiguration,
  smartlinkUnitSchema,
);
